<template>
    <FormSection title="Referral Information" :full-height="false" :application-id="application" class="mb-2">
        <template id="Referral_Source" #content>
            <div class="row p-2 pb-0">
                <div class="col-12 row ms-4">
                    <div class="form-group col-12 col-lg-5 pe-lg-2 d-flex flex-column">
                        <label for="line-of-service">
                            Line of Service
                            <span class="text-danger">*</span>
                        </label>
                        <div class="d-flex flex-wrap mt-1" v-if="lineOfServices.length > 0">
                            <div
                                v-for="lineOfService of lineOfServiceOptions"
                                class="form-check custom-radio form-check-inline d-inline"
                            >
                                <input
                                    type="radio"
                                    :id="`line-of-service-${lineOfService.name}`"
                                    class="form-check-input"
                                    name="line-of-service"
                                    :checked="patientDetails.lineOfService == lineOfService.value"
                                    :value="`${lineOfService.value}`"
                                    disabled
                                />
                                <label class="form-check-label" :for="`line-of-service-${lineOfService.name}`">
                                    <span class="me-1">{{ lineOfService.name }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2 d-flex flex-column">
                        <div class="form-group">
                            <label for="referral-date">
                                Referral Date
                                <span class="text-danger">*</span>
                            </label>
                            <DatePicker :getDate="handleDateChange">
                                <div class="input-group date datepickerInput">
                                    <input
                                        name="referralDate"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        autocomplete="off"
                                        :value="formatDateInput(patientDetails.referralDate)"
                                        @change="(e) => handleDateChange(e.target.value, e.target.name)"
                                        @blur="v$.patientDetails.referralDate.$touch"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                    <span
                                        v-if="v$.patientDetails.referralDate.$error"
                                        class="field-invalid"
                                        >{{
                                            v$.patientDetails.referralDate.$errors[0].$message
                                        }}</span
                                    >
                                </div>
                            </DatePicker>
                        </div>
                        <div class="form-group">
                            <label for="inquiry-date"> Inquiry Date </label>
                            <DatePicker>
                                <div class="input-group date datepickerInput">
                                    <input
                                        id="inquiry-date"
                                        name="inquiryDate"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(patientDetails.inquiryDate)"
                                        autocomplete="off"
                                        disabled
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                                <span v-if="!patientDetails.inquiryDate" class="text-secondary"
                                    >Not Applicable</span
                                >
                            </DatePicker>
                        </div>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-2">
                        <label for="referral-service-location"> Patient Current Location </label>
                        <input
                            class="form-control"
                            name="referral-service-location"
                            maxlength="50"
                            type="text"
                            placeholder="Enter Current Location"
                            v-model="patientDetails.patientCurrentLocation"
                        />
                    </div>
                </div>
                <div class="form-group col-10 d-flex flex-column ms-5">
                    <label for="source-of-referral">
                        Referral Source
                        <span class="text-danger">*</span>
                    </label>
                    <div class="d-flex flex-wrap" v-if="referralSources.length > 0">
                        <div
                            v-for="source of referralSources"
                            class="form-check custom-radio form-check-inline d-inline"
                        >
                            <input
                                type="radio"
                                :id="`source-of-referral-${source.name}`"
                                class="form-check-input"
                                name="source-of-referral"
                                :checked="patientDetails.sourceOfReferral === source.value"
                                :value="source.value"
                                v-model="patientDetails.sourceOfReferral"
                                @change="sourceOfReferralUpdated()"
                            />
                            <label class="form-check-label" :for="`source-of-referral-${source.name}`">
                                <span class="me-1">{{ source.name }}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <section class="col-10 card flex-row ms-5 row g-0 mb-2 p-2">
                    <template v-if="patientDetails.sourceOfReferral == 1">
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source">
                                <span>Facility Name</span>
                            </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getVendorList"
                                :value="facility"
                                v-on:valueChanged="updateFacility"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-name">
                                <span>Facility Point of Contact Name</span>
                            </label>
                            <input
                                class="form-control"
                                maxlength="50"
                                type="text"
                                v-model="patientDetails.facilityReferralSourceContactName"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getUsersByRole"
                                :value="communityLiaison"
                                v-on:valueChanged="updateCommunityLiaison"
                                :init-value="communityLiaison"
                            />
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group col-12 col-lg-4">
                            <button type="button" class="btn btn-primary" @click="handleFacilityRedirection()">
                                Add Facility
                            </button>
                        </div>
                    </template>
                    <template v-if="patientDetails.sourceOfReferral == 2">
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-source"> Referring Physician Name </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getPhysicianList"
                                :value="physician"
                                v-on:valueChanged="updatePhysician"
                                :init-value="physician"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-3">
                            <label for="referral-name">
                                <span>Physician Point of Contact Name</span>
                            </label>
                            <input
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Name"
                                v-model="patientDetails.physicianReferralSourceContactName"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getUsersByRole"
                                :value="communityLiaison"
                                v-on:valueChanged="updateCommunityLiaison"
                                :init-value="communityLiaison"
                            />
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group col-12 col-lg-4">
                            <div class="form-check custom-checkbox form-check-inline">
                                <input
                                    type="checkbox"
                                    id="referral-same-attending-physician"
                                    class="form-check-input au-target"
                                    v-model="patientDetails.sameAsAttendingPhysician"
                                />
                                <label class="form-check-label" for="referral-same-attending-physician">
                                    <span class="me-1">Will serve as Attending Physician</span>
                                </label>
                            </div>
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group col-12 col-lg-4">
                            <button type="button" class="btn btn-primary" @click="handlePhysicianRedirection()">
                                Add Physician
                            </button>
                        </div>
                    </template>
                    <template v-if="patientDetails.sourceOfReferral == 3">
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="internal-referral-source"> Other Referral Source Name </label>
                            <input
                                class="form-control"
                                maxlength="50"
                                type="text"
                                placeholder="Enter Name of Referral Source"
                                v-model="patientDetails.otherReferralSourceName"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="referral-source">
                                <span>Relationship</span>
                            </label>
                            <select
                                name="relationship"
                                class="form-select"
                                v-model="patientDetails.otherReferralSourceRelationship"
                            >
                                <option :value="null">Select Relationship</option>
                                <option v-for="rel of relationships" :value="rel.value">{{ rel.name }}</option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2" v-if="showRelationshipDescription">
                            <label for="referral-name">
                                <span>Describe Other Relationship</span>
                            </label>
                            <input
                                class="form-control"
                                type="text"
                                placeholder="Enter Relationship"
                                v-model="patientDetails.otherReferralSourceRelationshipDescription"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getUsersByRole"
                                :value="communityLiaison"
                                v-on:valueChanged="updateCommunityLiaison"
                                :init-value="communityLiaison"
                            />
                        </div>
                    </template>
                    <template v-if="patientDetails.sourceOfReferral == 4">
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="internal-referral-source-name"> Internal Referral Source Name </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getUsersByRole"
                                :value="communityLiaison"
                                v-on:valueChanged="updateCommunityLiaison"
                                :init-value="communityLiaison"
                            />
                        </div>
                        <div class="form-group col-12 col-lg-4 pe-lg-2">
                            <label for="internal-referral-source"> Internal Referral Source </label>
                            <select
                                name="internalReferralSource"
                                class="form-select"
                                v-model="patientDetails.internalReferralSource"
                            >
                                <option :value="null">Select Source</option>
                                <option v-for="data of InternalReferralSource" :value="data.value">
                                    {{ data.name }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group col-12 col-lg-3 pe-lg-2">
                            <label for="community-liaison"> Community Liaison </label>
                            <TypeaheadInput
                                placeholder="Start Typing..."
                                :getData="getUsersByRole"
                                :value="communityLiaison"
                                v-on:valueChanged="updateCommunityLiaison"
                                :init-value="communityLiaison"
                            />
                        </div>
                    </template>
                </section>
                <div class="w-100"></div>
                <div class="col-12 row ms-4">
                    <div class="form-group col-4">
                        <label for="referral-comments"> Comments </label>
                        <CommentInput
                            input-name="referral-comments"
                            :result="patientDetails.comments"
                            v-on:resultUpdatedAppended="resultUpdatedAppended"
                            v-on:resultUpdated="resultUpdated"
                            placeholder="Enter Comments"
                            :application="application"
                        >
                        </CommentInput>
                    </div>
                    <div class="form-group col-4">
                        <div class="mb-2">
                            <label for="pending-reason"> Pending Reason </label>
                            <select class="form-select" v-model="patientDetails.pendingReason">
                                <option :value="null">Select Pending Reason</option>
                                <option v-for="reason of pendingReasonsOptions" :value="reason.value">
                                    {{ reason.name }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <textarea
                                class="form-control"
                                rows="4"
                                v-model="patientDetails.pendingReasonComment"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </FormSection>
</template>

<script src="./ReferralInformationForm.ts"></script>
